import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { BreakEventStatus, CalendarSyncBreak, CalendarSyncPair } from 'services/selfSchedulingServices';
import { formatDate } from '../SyncCalendar/syncStatusDisplay';
import { pairBreaks } from './currentRun';
import MessageCell from './MessageCell';

/** MM-1198: columns of the breaks table under a location + provider. */
const BREAK_COLUMNS = ['Start date', 'End date', 'Break type', 'Event', 'Status', 'Received', 'Completed'];

/** MM-1198: the break event as shown - what happened to the break in DrChrono. */
const EVENT_LABELS: Record<CalendarSyncBreak['eventType'], string> = {
  APPOINTMENT_CREATE: 'Added',
  APPOINTMENT_MODIFY: 'Updated',
  APPOINTMENT_DELETE: 'Removed',
};

/** MM-1198: label and chip color of each break status. */
const BREAK_STATUS_CHIPS: Record<
  BreakEventStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  PENDING: { label: 'Pending', color: 'default' },
  INPROGRESS: { label: 'In progress', color: 'info' },
  COMPLETED: { label: 'Completed', color: 'success' },
  FAILURE: { label: 'Failed', color: 'error' },
  // MM-1198: a newer break of the same location + provider + exam room was rebuilt instead - nothing processed twice
  SUPERSEDED: { label: 'Superseded', color: 'default' },
  IGNORED: { label: 'Ignored', color: 'warning' },
  // no longer stored (every break triggers a full rebuild); rows stored before still show until 6:00 AM
  EXPIRED: { label: 'Expired', color: 'default' },
};

/** MM-1198: the break type as shown - from the stored recurring_appointment. */
const breakTypeLabel = (item: CalendarSyncBreak) => (item.recurring ? 'Recurring' : 'Single');

/**
 * MM-1198: a location + provider's breaks - each one triggers a full rebuild of the location + provider, so they are
 * not split by profile. Newest first by received date and time, whatever the status (the worker also takes the newest
 * first); the row id breaks a tie.
 */
export const sortedBreaks = (pair: CalendarSyncPair): CalendarSyncBreak[] =>
  pairBreaks(pair).sort((a, b) => (b.receivedDate ?? 0) - (a.receivedDate ?? 0) || b.eventId - a.eventId);

/**
 * MM-1198: the DrChrono breaks received by webhook and stored in the break table since the 6:00 AM clean-up for one
 * location + provider - its start and end (Central time), break type, event (Added / Updated / Removed) and stored
 * status - so it is visible whether the full rebuild each break triggered ran (Completed), is still waiting (Pending /
 * In progress), failed (Failed), was covered by a newer break (Superseded) or was ignored.
 * pair - the location + provider
 */
const BreakEventsTable = ({ pair }: { pair: CalendarSyncPair }) => {
  const rows = sortedBreaks(pair);
  if (!rows.length) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
        No breaks received for this location and provider since the 6:00 AM clean-up.
      </Typography>
    );
  }
  return (
    <Box sx={{ m: 1, ml: 4, border: '1px solid #EAECF0', borderRadius: 1, backgroundColor: '#FCFCFD' }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {BREAK_COLUMNS.map((column) => (
              <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((item) => {
            const chip = BREAK_STATUS_CHIPS[item.status] ?? BREAK_STATUS_CHIPS.PENDING;
            return (
              <TableRow key={item.eventId} sx={{ backgroundColor: item.status === 'FAILURE' ? '#FEF3F2' : undefined }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.startDate)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.endDate)}</TableCell>
                <TableCell>{breakTypeLabel(item)}</TableCell>
                <TableCell>{EVENT_LABELS[item.eventType] ?? item.eventType}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip size='small' color={chip.color} label={chip.label} />
                    {/* MM-1198: why it failed (red) or was ignored (grey) - an icon that opens the full message */}
                    {item.errorMessage && (item.status === 'FAILURE' || item.status === 'IGNORED') ? (
                      <MessageCell
                        message={item.errorMessage}
                        title={item.status === 'FAILURE' ? 'Why the break failed' : 'Why the break was ignored'}
                        error={item.status === 'FAILURE'}
                        iconOnly
                      />
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.receivedDate)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.completedDate)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default BreakEventsTable;
